import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Check, ChevronDown, Lock } from "lucide-react";
import { formatCurrency } from "@/utils/currency";
import type {
  ProgramPageBlock,
  ProgramPageCta,
  ProgramPageDocument,
  ProgramPageProgram,
  ProgramPageStanding,
  ProgramPageTier,
  ProgramPageTierItem,
  TiersBlock,
} from "@/types/programPage";
import { PageIcon } from "./icons";
import { pageThemeStyle, readableOn } from "./theme";
import { formatCommission } from "./templates";

/**
 * Renders a program page document. The one renderer behind the public page,
 * the affiliate page and the editor preview, so what a brand designs is what
 * ships.
 *
 * Two rules keep it honest:
 *  - Colours come only from the document's theme, as `--pp-*` variables scoped
 *    to the root. No app tokens, no `dark:` — the page looks the same in any
 *    shell.
 *  - Layout responds to the *container*, not the viewport (auto-fit grids,
 *    wrapping flex), because the editor previews a phone inside a desktop
 *    window and the affiliate shell takes 280px for its sidebar.
 */

export interface ProgramPageRendererProps {
  document: ProgramPageDocument;
  programs: ProgramPageProgram[];
  /** What a tiers block with `programId: null` resolves to. */
  defaultProgramId: string | null;
  /** Keyed by program id. Empty for visitors. */
  standing?: Record<string, ProgramPageStanding>;
  /** Where "join" buttons go. Null hides them (the viewer is already in). */
  joinUrl: string | null;
  /** Editor only: outline the selected block and make blocks clickable. */
  selectedBlockId?: string | null;
  onSelectBlock?: (id: string) => void;
  className?: string;
}

const autoFit = (min: number): CSSProperties => ({
  display: "grid",
  gap: "1rem",
  gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}px), 1fr))`,
});

// The app's type scale: the same weights and sizes as the rest of the
// dashboard, sized off the container (see the root's containerType) so the
// editor's phone preview scales too.
const heading: CSSProperties = { letterSpacing: "-0.01em" };
const H1_SIZE: CSSProperties = { fontSize: "clamp(1.75rem, 5.5cqw, 2.5rem)", lineHeight: 1.15 };
const H2_SIZE: CSSProperties = { fontSize: "clamp(1.375rem, 3.6cqw, 1.75rem)", lineHeight: 1.25 };

// The app's card: bg-card, border-border, rounded-xl, shadow-sm.
const card: CSSProperties = {
  background: "var(--pp-surface)",
  border: "1px solid var(--pp-border)",
  borderRadius: "0.75rem",
  boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
};

export function ProgramPageRenderer({
  document,
  programs,
  defaultProgramId,
  standing = {},
  joinUrl,
  selectedBlockId,
  onSelectBlock,
  className = "",
}: ProgramPageRendererProps) {
  // Numbered sections count only blocks that carry a heading of their own.
  const numbers = useMemo(() => {
    const map = new Map<string, string>();
    if (!document.theme.sectionNumbers) return map;
    let n = 0;
    for (const block of document.blocks) {
      if ("heading" in block && block.heading) {
        map.set(block.id, String(++n).padStart(2, "0"));
      }
    }
    return map;
  }, [document]);

  return (
    <div
      className={`w-full ${className}`}
      style={{ ...pageThemeStyle(document.theme), containerType: "inline-size" }}
    >
      <div
        className="mx-auto flex w-full max-w-5xl flex-col px-5"
        style={{ gap: "clamp(2.5rem, 6cqw, 4rem)", paddingBlock: "clamp(1.5rem, 4cqw, 2.5rem)" }}
      >
        {document.blocks.map((block) => {
          // A closing banner whose only point is "join" says nothing to someone
          // who already has, so it goes with its button.
          if (block.type === "cta" && block.cta?.action === "join" && !joinUrl) return null;
          const selected = selectedBlockId === block.id;
          return (
            <section
              key={block.id}
              data-block-id={block.id}
              onClick={onSelectBlock ? () => onSelectBlock(block.id) : undefined}
              className={onSelectBlock ? "cursor-pointer scroll-mt-6" : undefined}
              style={
                selected
                  ? { outline: "2px solid var(--pp-accent)", outlineOffset: 12, borderRadius: 4 }
                  : undefined
              }
            >
              <BlockView
                block={block}
                number={numbers.get(block.id)}
                programs={programs}
                defaultProgramId={defaultProgramId}
                standing={standing}
                joinUrl={joinUrl}
                interactive={!onSelectBlock}
              />
            </section>
          );
        })}
      </div>
    </div>
  );
}

interface BlockViewProps {
  block: ProgramPageBlock;
  number?: string;
  programs: ProgramPageProgram[];
  defaultProgramId: string | null;
  standing: Record<string, ProgramPageStanding>;
  joinUrl: string | null;
  /** False in the editor, where a click selects instead of navigating. */
  interactive: boolean;
}

function BlockView({ block, number, interactive, joinUrl, ...data }: BlockViewProps) {
  const button = (cta: ProgramPageCta | null) => (
    <CtaButton cta={cta} joinUrl={joinUrl} interactive={interactive} />
  );

  switch (block.type) {
    case "hero": {
      const copy = (
        <div
          className={`flex flex-col gap-5 ${
            block.layout === "split" ? "items-start text-left" : "items-center text-center"
          }`}
        >
          {block.eyebrow && <Eyebrow>{block.eyebrow}</Eyebrow>}
          {block.title && (
            <h1
              className="font-semibold"
              style={{ ...heading, ...H1_SIZE, overflowWrap: "anywhere" }}
            >
              {block.title}
            </h1>
          )}
          {block.subtitle && (
            <p
              className="max-w-2xl whitespace-pre-line text-base"
              style={{ color: block.layout === "cover" && block.imageUrl ? "inherit" : "var(--pp-muted)" }}
            >
              {block.subtitle}
            </p>
          )}
          {button(block.cta)}
        </div>
      );

      if (block.layout === "cover" && block.imageUrl) {
        return (
          <div
            className="relative isolate flex min-h-[420px] items-center justify-center overflow-hidden px-6 py-16"
            style={{ borderRadius: "0.75rem", color: "#ffffff" }}
          >
            <img src={block.imageUrl} alt="" className="absolute inset-0 -z-20 h-full w-full object-cover" />
            <div className="absolute inset-0 -z-10 bg-black/55" />
            {copy}
          </div>
        );
      }
      if (block.layout === "split" && block.imageUrl) {
        return (
          <div className="flex flex-wrap items-center gap-10">
            <div className="min-w-[280px] flex-1">{copy}</div>
            <img
              src={block.imageUrl}
              alt=""
              className="aspect-[4/5] min-w-[260px] flex-1 object-cover"
              style={{ borderRadius: "0.75rem", maxHeight: 520 }}
            />
          </div>
        );
      }
      return (
        <div className="flex flex-col gap-10">
          {copy}
          {block.imageUrl && (
            <img
              src={block.imageUrl}
              alt=""
              className="max-h-[480px] w-full object-cover"
              style={{ borderRadius: "0.75rem" }}
            />
          )}
        </div>
      );
    }

    case "tiers":
      return <TiersView block={block} number={number} {...data} />;

    case "features":
      return (
        <div>
          <SectionHeading number={number} title={block.heading} sub={block.subheading} />
          <div style={autoFit(block.columns === 2 ? 320 : block.columns === 3 ? 240 : 190)}>
            {block.items.map((item) => (
              <div key={item.id} className="flex flex-col gap-3 p-6" style={card}>
                {item.icon && (
                  <span
                    className="flex h-11 w-11 items-center justify-center text-xl"
                    style={{
                      borderRadius: "0.625rem",
                      background: "var(--pp-accent-soft)",
                      color: "var(--pp-accent)",
                    }}
                  >
                    <PageIcon icon={item.icon} />
                  </span>
                )}
                {item.title && <h3 className="text-lg font-semibold" style={heading}>{item.title}</h3>}
                {item.text && (
                  <p className="whitespace-pre-line text-sm leading-relaxed" style={{ color: "var(--pp-muted)" }}>
                    {item.text}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      );

    case "steps":
      return (
        <div>
          <SectionHeading number={number} title={block.heading} sub={block.subheading} />
          <ol className="flex flex-col">
            {block.items.map((item, i) => (
              <li
                key={item.id}
                className="flex gap-5 py-5"
                style={{ borderTop: i ? "1px solid var(--pp-border)" : undefined }}
              >
                <span
                  className="w-10 shrink-0 text-xl font-semibold tabular-nums"
                  style={{ color: "var(--pp-accent)" }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex flex-col gap-1">
                  {item.title && <h3 className="text-lg font-semibold" style={heading}>{item.title}</h3>}
                  {item.text && (
                    <p className="whitespace-pre-line leading-relaxed" style={{ color: "var(--pp-muted)" }}>
                      {item.text}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      );

    case "stats":
      return (
        <div className="p-6" style={{ ...card, ...autoFit(150) }}>
          {block.items.map((item) => (
            <div key={item.id} className="flex flex-col items-center gap-1 text-center">
              <span className="text-3xl font-semibold" style={{ color: "var(--pp-accent)", ...heading }}>
                {item.value}
              </span>
              <span className="text-sm" style={{ color: "var(--pp-muted)" }}>{item.label}</span>
            </div>
          ))}
        </div>
      );

    case "text":
      return (
        <div className={block.align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-3xl"}>
          <SectionHeading number={number} title={block.heading} center={block.align === "center"} />
          <p className="whitespace-pre-line text-base leading-relaxed" style={{ color: "var(--pp-muted)" }}>
            {block.body}
          </p>
        </div>
      );

    case "image":
      return block.imageUrl ? (
        <figure className="flex flex-col gap-3">
          <img
            src={block.imageUrl}
            alt={block.alt}
            loading="lazy"
            className="w-full object-cover"
            style={{ borderRadius: "0.75rem" }}
          />
          {block.caption && (
            <figcaption className="text-center text-sm" style={{ color: "var(--pp-muted)" }}>
              {block.caption}
            </figcaption>
          )}
        </figure>
      ) : (
        <Placeholder>Add an image</Placeholder>
      );

    case "faq":
      return (
        <div className="mx-auto w-full max-w-3xl">
          <SectionHeading number={number} title={block.heading} />
          <div className="flex flex-col gap-3">
            {block.items.map((item) => (
              <FaqRow key={item.id} question={item.question} answer={item.answer} />
            ))}
          </div>
        </div>
      );

    case "cta":
      return (
        <div
          className="flex flex-col items-center gap-5 px-6 py-12 text-center"
          style={{
            borderRadius: "0.75rem",
            background: "var(--pp-accent)",
            color: "var(--pp-on-accent)",
          }}
        >
          {block.title && (
            <h2 className="font-semibold" style={{ ...heading, ...H2_SIZE }}>{block.title}</h2>
          )}
          {block.subtitle && <p className="max-w-xl whitespace-pre-line opacity-80">{block.subtitle}</p>}
          <CtaButton cta={block.cta} joinUrl={joinUrl} interactive={interactive} inverted />
        </div>
      );
  }
}

// ─── Tiers ───────────────────────────────────────────────────────────────────

type RungState = "reached" | "current" | "next" | "locked" | "none";

interface Rung {
  item: ProgramPageTierItem;
  tier: ProgramPageTier | null;
  name: string;
  commission: string;
  color: string;
  state: RungState;
}

function TiersView({
  block,
  number,
  programs,
  defaultProgramId,
  standing,
}: {
  block: TiersBlock;
  number?: string;
  programs: ProgramPageProgram[];
  defaultProgramId: string | null;
  standing: Record<string, ProgramPageStanding>;
}) {
  const program = programs.find((p) => p.id === (block.programId ?? defaultProgramId)) ?? null;
  const mine = program && block.showProgress ? standing[program.id] ?? null : null;

  const rungs: Rung[] = block.items.map((item) => {
    const tier = program?.tiers.find((t) => t.id === item.tierId) ?? null;
    let state: RungState = "none";
    if (mine && tier) {
      if (tier.id === mine.currentTierId) state = "current";
      else if (tier.id === mine.nextTierId) state = "next";
      else state = tier.threshold <= mine.totalSales ? "reached" : "locked";
    }
    return {
      item,
      tier,
      state,
      name: item.name || tier?.name || "Untitled tier",
      commission: item.commissionLabel || (tier ? formatCommission(program, tier.commissionValue) : ""),
      color: item.color || "var(--pp-accent)",
    };
  });

  return (
    <div>
      <SectionHeading number={number} title={block.heading} sub={block.subheading} />
      {mine && program && <StandingBanner program={program} standing={mine} rungs={rungs} />}
      {!block.items.length ? (
        <Placeholder>Add a tier to this block</Placeholder>
      ) : block.layout === "table" ? (
        <TierTable rungs={rungs} showThresholds={block.showThresholds} />
      ) : block.layout === "ladder" ? (
        <div className="flex flex-col">
          {rungs.map((rung, i) => (
            <div key={rung.item.id} className="flex gap-5">
              <div className="flex flex-col items-center">
                <RungBadge rung={rung} />
                {i < rungs.length - 1 && (
                  <span className="w-px flex-1" style={{ background: "var(--pp-border)" }} />
                )}
              </div>
              <div className="min-w-0 flex-1 pb-8">
                <TierCard rung={rung} showThresholds={block.showThresholds} mine={mine} flat />
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Four rungs sit in one row rather than wrapping to 3 + 1.
        <div style={{ ...autoFit(rungs.length === 4 ? 210 : 250), alignItems: "stretch" }}>
          {rungs.map((rung) => (
            <TierCard key={rung.item.id} rung={rung} showThresholds={block.showThresholds} mine={mine} />
          ))}
        </div>
      )}
    </div>
  );
}

/** "You're a Creator — $450 to Athlete", with the bar to prove it. */
function StandingBanner({
  program,
  standing,
  rungs,
}: {
  program: ProgramPageProgram;
  standing: ProgramPageStanding;
  rungs: Rung[];
}) {
  const nameOf = (tierId: string | null) =>
    rungs.find((r) => r.tier?.id === tierId)?.name ??
    program.tiers.find((t) => t.id === tierId)?.name ??
    null;
  const current = nameOf(standing.currentTierId);
  const next = nameOf(standing.nextTierId);

  return (
    <div className="mb-6 flex flex-col gap-3 p-5" style={card}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <p className="text-base font-semibold" style={heading}>
          {current ? `You're ${/^[aeiou]/i.test(current) ? "an" : "a"} ${current}` : "Your climb starts here"}
        </p>
        <p className="text-sm" style={{ color: "var(--pp-muted)" }}>
          {formatCurrency(standing.totalSales)} in sales
          {next
            ? ` · ${formatCurrency(standing.remainingToNext)} to ${next}`
            : program.tiers.length
              ? " · top tier reached"
              : ""}
        </p>
      </div>
      <div
        className="h-2.5 w-full overflow-hidden"
        style={{ borderRadius: 999, background: "var(--pp-border)" }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(standing.progress)}
        aria-label={next ? `Progress to ${next}` : "Tier progress"}
      >
        <div
          className="h-full transition-[width] duration-700"
          style={{ width: `${standing.progress}%`, borderRadius: 999, background: "var(--pp-accent)" }}
        />
      </div>
    </div>
  );
}

function RungBadge({ rung, size = 48 }: { rung: Rung; size?: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.46,
        borderRadius: "0.625rem",
        background: rung.color,
        color: rung.item.color ? readableOn(rung.item.color) : "var(--pp-on-accent)",
        opacity: rung.state === "locked" ? 0.55 : 1,
      }}
    >
      {rung.item.icon ? <PageIcon icon={rung.item.icon} /> : <span className="font-semibold">{rung.name.charAt(0)}</span>}
    </span>
  );
}

function StateChip({ state }: { state: RungState }) {
  if (state === "none") return null;
  const label = { current: "You're here", next: "Up next", reached: "Unlocked", locked: "Locked" }[state];
  const solid = state === "current";
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold"
      style={{
        borderRadius: 999,
        background: solid ? "var(--pp-accent)" : "transparent",
        color: solid ? "var(--pp-on-accent)" : "var(--pp-muted)",
        border: solid ? undefined : "1px solid var(--pp-border)",
      }}
    >
      {state === "reached" && <Check className="h-3 w-3" aria-hidden />}
      {state === "locked" && <Lock className="h-3 w-3" aria-hidden />}
      {label}
    </span>
  );
}

function TierCard({
  rung,
  showThresholds,
  mine,
  flat = false,
}: {
  rung: Rung;
  showThresholds: boolean;
  mine: ProgramPageStanding | null;
  /** Ladder layout: the badge sits on the rail, so the card omits it. */
  flat?: boolean;
}) {
  const { item, tier, state } = rung;
  return (
    <article
      className="flex h-full flex-col overflow-hidden"
      style={{
        ...card,
        borderColor: state === "current" ? rung.color : "var(--pp-border)",
        boxShadow: state === "current" ? `0 0 0 1px ${rung.color}, 0 18px 40px -24px ${rung.color}` : undefined,
      }}
    >
      {item.imageUrl ? (
        <img src={item.imageUrl} alt="" loading="lazy" className="h-36 w-full object-cover" />
      ) : (
        <span className="h-1.5 w-full" style={{ background: rung.color }} />
      )}
      <div className="flex flex-1 flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-3">
          {!flat && <RungBadge rung={rung} />}
          <div className="flex flex-wrap justify-end gap-1.5">
            {item.badge && (
              <span
                className="px-2.5 py-1 text-xs font-semibold"
                style={{
                  borderRadius: 999,
                  background: `color-mix(in srgb, ${rung.color} 18%, transparent)`,
                  color: "var(--pp-text)",
                }}
              >
                {item.badge}
              </span>
            )}
            <StateChip state={state} />
          </div>
        </div>

        <div>
          <h3 className="text-xl font-semibold" style={{ ...heading, overflowWrap: "anywhere" }}>
            {rung.name}
          </h3>
          {item.tagline && (
            <p className="mt-1 text-sm" style={{ color: "var(--pp-muted)" }}>{item.tagline}</p>
          )}
        </div>

        {rung.commission && (
          <p className="flex items-baseline gap-2">
            <span className="text-3xl font-semibold" style={{ color: rung.color }}>{rung.commission}</span>
            <span className="text-sm" style={{ color: "var(--pp-muted)" }}>commission</span>
          </p>
        )}

        {showThresholds && tier && (
          <p className="text-sm" style={{ color: "var(--pp-muted)" }}>
            From {formatCurrency(tier.threshold).replace(/\.00$/, "")} in total sales
          </p>
        )}

        {state === "next" && mine && (
          <div className="flex flex-col gap-1.5">
            <div className="h-1.5 w-full overflow-hidden" style={{ borderRadius: 999, background: "var(--pp-border)" }}>
              <div className="h-full" style={{ width: `${mine.progress}%`, background: rung.color, borderRadius: 999 }} />
            </div>
            <p className="text-xs font-medium">{formatCurrency(mine.remainingToNext)} to go</p>
          </div>
        )}

        <TierList title={item.requirementsTitle} lines={item.requirements} color={rung.color} marker="dot" />
        <TierList title={item.rewardsTitle} lines={item.rewards} color={rung.color} marker="check" />
      </div>
    </article>
  );
}

function TierList({
  title,
  lines,
  color,
  marker,
}: {
  title: string;
  lines: string[];
  color: string;
  marker: "dot" | "check";
}) {
  if (!lines.length) return null;
  return (
    <div className="flex flex-col gap-2">
      {title && (
        <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--pp-muted)" }}>
          {title}
        </p>
      )}
      <ul className="flex flex-col gap-1.5 text-sm">
        {lines.map((line, i) => (
          <li key={i} className="flex gap-2">
            {marker === "check" ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color }} aria-hidden />
            ) : (
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
            )}
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TierTable({ rungs, showThresholds }: { rungs: Rung[]; showThresholds: boolean }) {
  const cell = "px-4 py-4 align-top text-left";
  return (
    <div className="overflow-x-auto" style={card}>
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr style={{ color: "var(--pp-muted)" }}>
            <th className={`${cell} text-xs font-semibold uppercase tracking-wider`}>Tier</th>
            <th className={`${cell} text-xs font-semibold uppercase tracking-wider`}>Commission</th>
            {showThresholds && (
              <th className={`${cell} text-xs font-semibold uppercase tracking-wider`}>Sales needed</th>
            )}
            <th className={`${cell} text-xs font-semibold uppercase tracking-wider`}>What you unlock</th>
          </tr>
        </thead>
        <tbody>
          {rungs.map((rung) => (
            <tr
              key={rung.item.id}
              style={{
                borderTop: "1px solid var(--pp-border)",
                background:
                  rung.state === "current" ? `color-mix(in srgb, ${rung.color} 10%, transparent)` : undefined,
              }}
            >
              <td className={cell}>
                <div className="flex items-center gap-3">
                  <RungBadge rung={rung} size={36} />
                  <div className="flex flex-col items-start gap-1">
                    <span className="text-base font-semibold" style={heading}>{rung.name}</span>
                    <StateChip state={rung.state} />
                  </div>
                </div>
              </td>
              <td className={`${cell} text-lg font-semibold`} style={{ color: rung.color }}>
                {rung.commission || "—"}
              </td>
              {showThresholds && (
                <td className={cell}>
                  {rung.tier ? formatCurrency(rung.tier.threshold).replace(/\.00$/, "") : rung.item.badge || "—"}
                </td>
              )}
              <td className={cell}>
                {rung.item.rewards.length ? rung.item.rewards.join(" · ") : rung.item.tagline || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Shared pieces ───────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span
      className="px-3 py-1 text-xs font-medium uppercase tracking-wider"
      style={{
        borderRadius: 999,
        border: "1px solid var(--pp-border)",
        color: "var(--pp-accent)",
      }}
    >
      {children}
    </span>
  );
}

function SectionHeading({
  number,
  title,
  sub,
  center = false,
}: {
  number?: string;
  title: string;
  sub?: string;
  center?: boolean;
}) {
  if (!title && !sub) return null;
  return (
    <header className={`mb-6 flex flex-col gap-1.5 ${center ? "items-center" : ""}`}>
      {number && (
        <span className="text-xs font-semibold tabular-nums tracking-widest" style={{ color: "var(--pp-accent)" }}>
          {number}
        </span>
      )}
      {title && (
        <h2 className="font-semibold" style={{ ...heading, ...H2_SIZE, overflowWrap: "anywhere" }}>
          {title}
        </h2>
      )}
      {sub && (
        <p className="max-w-2xl whitespace-pre-line" style={{ color: "var(--pp-muted)" }}>{sub}</p>
      )}
    </header>
  );
}

function CtaButton({
  cta,
  joinUrl,
  interactive,
  inverted = false,
}: {
  cta: ProgramPageCta | null;
  joinUrl: string | null;
  interactive: boolean;
  /** On the accent-filled CTA block the button flips to stay visible. */
  inverted?: boolean;
}) {
  if (!cta) return null;
  const href = cta.action === "url" ? cta.url : joinUrl;
  // A join button means nothing to someone who already joined.
  if (!href) return null;
  return (
    <a
      href={interactive ? href : undefined}
      target={cta.action === "url" ? "_blank" : undefined}
      rel="noopener noreferrer"
      className="inline-flex h-10 items-center justify-center px-5 text-sm font-medium shadow transition-opacity hover:opacity-90"
      style={{
        borderRadius: "0.5rem",
        background: inverted ? "var(--pp-on-accent)" : "var(--pp-accent)",
        color: inverted ? "var(--pp-accent)" : "var(--pp-on-accent)",
        ...heading,
      }}
    >
      {cta.label}
    </a>
  );
}

function FaqRow({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={card}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-semibold"
      >
        <span>{question}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          style={{ color: "var(--pp-accent)" }}
          aria-hidden
        />
      </button>
      {open && (
        <p className="whitespace-pre-line px-5 pb-5 leading-relaxed" style={{ color: "var(--pp-muted)" }}>
          {answer}
        </p>
      )}
    </div>
  );
}

function Placeholder({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex min-h-[140px] items-center justify-center text-sm"
      style={{
        borderRadius: "0.75rem",
        border: "1.5px dashed var(--pp-border)",
        color: "var(--pp-muted)",
      }}
    >
      {children}
    </div>
  );
}
